import { useMemo } from "react";
import { useTiers } from "../../../hooks/useTiers";
import "./pointsOutcomeView.scss";

const formatSlug = (slug) =>
  slug
    ? slug
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    : "";

const AwardFlatPointsView = ({ data, definition, customComponentName }) => {
  // Points data lives in outcomeRules[].ruleData.points_calculation (not in outcomeData).
  // Also handle legacy formats: data.points_calculation or double-nested.
  const pointsCalc = useMemo(() => {
    // Primary: extract from outcomeRules ruleData
    const rules = definition?.outcomeRules || [];
    for (const rule of rules) {
      const pc = rule?.ruleData?.points_calculation;
      if (pc && pc.rule_type) return pc;
    }
    // Fallback: try outcomeData.points_calculation (legacy)
    const pc = data?.points_calculation || {};
    return pc.points_calculation || pc;
  }, [data, definition?.outcomeRules]);

  const flatPoints = useMemo(() => pointsCalc.flat_points || {}, [pointsCalc]);

  const tierValues = useMemo(() => flatPoints.tier_values || {}, [flatPoints]);

  const boosterMultipliers = useMemo(
    () => pointsCalc.booster_multipliers || [],
    [pointsCalc]
  );

  const boosterFlatPoints = useMemo(
    () => pointsCalc.booster_flat_points || [],
    [pointsCalc]
  );

  const { tiers } = useTiers();

  const hasTierValues = Object.keys(tierValues).length > 0;

  const tierDisplay = useMemo(() => {
    if (!hasTierValues) return [];
    return Object.entries(tierValues).map(([tierId, points]) => {
      const tier = tiers.find((t) => t.id === tierId);
      return { name: tier?.name || tierId, points };
    });
  }, [tierValues, tiers, hasTierValues]);

  const totalBoostersCount = useMemo(
    () => boosterMultipliers.length + boosterFlatPoints.length,
    [boosterMultipliers, boosterFlatPoints]
  );

  return (
    <div className="points-outcome-view" data-testid="award-flat-points-view">
      <h5>
        {definition?.outcomeDefinition?.name || formatSlug(customComponentName)}
      </h5>
      <ul className="outcome-summary-list">
        <li>
          Basic Configuration:{" "}
          <strong>{hasTierValues ? "Per Tier" : "Fixed"}</strong>
          {!hasTierValues && flatPoints.default_value != null && (
            <>
              {" "}
              - <strong>{flatPoints.default_value} points</strong>
            </>
          )}
          {hasTierValues && tierDisplay.length > 0 && (
            <ul className="outcome-summary-sub-list">
              {tierDisplay.map((t) => (
                <li key={t.name}>
                  {t.name}: <strong>{t.points} points</strong>
                </li>
              ))}
            </ul>
          )}
        </li>
        <li>
          Time-Bound Boosters: <strong>{totalBoostersCount}</strong>
        </li>
      </ul>
    </div>
  );
};

export default AwardFlatPointsView;
